import { Page, Text, View, StyleSheet, Image } from "@react-pdf/renderer";
import dayjs from "dayjs";
import logo from "@/img/lansing-logo.jpeg";
import LoyaltyRebateInvoiceResponse from "@/app/interfaces/response/LoyaltyRebateInvoiceResponse";
import CommonHelper from "@/app/helpers/CommonHelper";

const styles = StyleSheet.create({
  page: {
    fontFamily: "Helvetica",
    paddingTop: 60,
    paddingHorizontal: 18,
    paddingBottom: 30,
    fontSize: 8,
  },
  headerFixed: {
    position: "absolute",
    top: 18,
    left: 18,
    right: 18,
    paddingBottom: 8,
    flexDirection: "row",
    alignItems: "center",
  },
  logo: { width: 120, height: 32, objectFit: "contain" },
  h1: { fontSize: 10, fontWeight: 700, marginBottom: 10 },
  bold: { fontWeight: 700 },
  labelRow: { flexDirection: "row", marginBottom: 2 },
  label: { width: 100 },
  value: { fontWeight: 700 },
  addressSection: { marginVertical: 10 },
  sectionTitle: {
    fontSize: 9,
    fontWeight: 700,
    marginBottom: 4,
  },
  table: {
    width: "100%",
    marginTop: 10,
  },
  thead: {
    flexDirection: "row",
    backgroundColor: "#f3f3f3",
    fontWeight: 700,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#000",
  },
  tr: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderColor: "#000",
    alignItems: "stretch",
    minHeight: 20,
  },
  td: {
    padding: 4,
    borderRightWidth: 1,
    borderColor: "#000",
    justifyContent: "center",
  },
  cFirst: {
    borderLeftWidth: 1,
  },
  right: { textAlign: "right" },
  redText: { color: "red" },
  totalsArea: { marginTop: 15, alignItems: "flex-end" },
  totalRow: {
    flexDirection: "row",
    width: 200,
    justifyContent: "space-between",
    marginBottom: 2,
  },
  cLine: { width: "6%" },
  cItem: { width: "10%" },
  cDesc: { width: "30%" },
  cColor: { width: "10%" },
  cQty: { width: "8%" },
  cAvail: { width: "8%" },
  cUom: { width: "8%" },
  cPrice: { width: "10%" },
  cTotal: { width: "10%" },
});

const AddressBlock = ({
  label,
  name,
  address1,
  address2,
  city,
  state,
  zip,
  country,
}: {
  label: string;
  name?: string | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
}) => (
  <View style={styles.labelRow}>
    <Text style={styles.label}>{label}:</Text>

    <View style={{ flexDirection: "column", flex: 1 }}>
      {name ? <Text style={styles.value}>{name}</Text> : null}
      {address1 ? <Text style={styles.value}>{address1}</Text> : null}
      {address2 ? <Text style={styles.value}>{address2}</Text> : null}

      {(!!city || !!state || !!zip) && (
        <Text style={styles.value}>
          {(city || "").toString().trim()}
          {state ? ` , ${state}` : ""}
          {zip ? ` , ${zip}` : ""}
        </Text>
      )}
      {country && <Text style={styles.value}>{country}</Text>}
    </View>
  </View>
);

const LabelValue = ({ label, value }: { label: string; value?: string }) => (
  <Text>
    <Text style={styles.label}>{label}: </Text>
    <Text style={styles.value}>{value ?? "-"}</Text>
  </Text>
);

const QualifyingInvoicePagePDF = ({
  invoice,
}: {
  invoice: LoyaltyRebateInvoiceResponse;
}) => {
  const formatDate = (date?: string) =>
    date ? dayjs(date).format("dddd, MMMM D, YYYY") : "-";

  return (
    <Page size="A4" style={styles.page}>
      <View style={styles.headerFixed} fixed>
        <Image src={logo} style={styles.logo} />
      </View>
      <Text style={styles.h1}>Order #{invoice.customOrderNumber}</Text>
      <View>
        <View style={styles.labelRow}>
          <LabelValue label="Order Type" value={invoice.inforOrderType} />
        </View>
        <View style={styles.labelRow}>
          <LabelValue label="Order Status" value={invoice.orderStatus} />
        </View>
        <View style={styles.labelRow}>
          <LabelValue
            label="Order Created Date"
            value={formatDate(invoice.createdOn)}
          />
        </View>
        <View style={styles.labelRow}>
          <LabelValue
            label="Order Invoice Date"
            value={formatDate(invoice.invoiceDate)}
          />
        </View>
        <View style={styles.labelRow}>
          <LabelValue
            label="Invoice Number"
            value={invoice.customOrderNumber}
          />
        </View>
        <View style={styles.labelRow}>
          <LabelValue
            label="Order Total"
            value={CommonHelper.addDolarSign(invoice.orderTotal)}
          />
        </View>
        <View style={styles.labelRow}>
          <LabelValue label="Job Name" value={invoice.jobName} />
        </View>
        <View style={styles.labelRow}>
          <LabelValue label="Branch" value={invoice.warehouse} />
        </View>
        <View style={styles.labelRow}>
          <LabelValue
            label="Delivery or Pickup"
            value={invoice.pickUpInStore ? "Pickup" : "Delivery"}
          />
        </View>
      </View>
      {invoice?.pickUpInStore ? (
        <AddressBlock
          label="Pickup point address"
          name={invoice.pickupAddress?.firstName}
          address1={invoice.pickupAddress?.address1}
          address2={invoice.pickupAddress?.address2 || undefined}
          city={invoice.pickupAddress?.city}
          state={invoice.pickupAddress?.stateProvinceName || undefined}
          zip={invoice.pickupAddress?.zipPostalCode || undefined}
          country={invoice.pickupAddress?.county}
        />
      ) : (
        <AddressBlock
          label="Delivery Address"
          name={invoice.shippingAddress?.firstName}
          address1={invoice.shippingAddress?.address1}
          address2={invoice.shippingAddress?.address2 || undefined}
          city={invoice.shippingAddress?.city}
          state={invoice.shippingAddress?.stateProvinceName || undefined}
          zip={invoice.shippingAddress?.zipPostalCode || undefined}
          country={invoice.shippingAddress?.county}
        />
      )}
      <View style={styles.addressSection}>
        <Text style={styles.sectionTitle}>Billing Address: </Text>
        <View style={styles.labelRow}>
          <LabelValue label="Name" value={invoice.billingAddress.firstName} />
        </View>
        <View style={styles.labelRow}>
          <LabelValue label="Number" value={invoice.customerNumber} />
        </View>
        <View style={styles.labelRow}>
          <LabelValue
            label="Phone"
            value={invoice.billingAddress.phoneNumber}
          />
        </View>
        <View style={styles.labelRow}>
          <LabelValue
            label="Address1"
            value={invoice.billingAddress.address1}
          />
        </View>
        <View style={styles.labelRow}>
          <LabelValue
            label="Address2"
            value={invoice.billingAddress.address2}
          />
        </View>
        <View style={styles.labelRow}>
          <LabelValue label="City" value={invoice.billingAddress.city} />
        </View>
        <View style={styles.labelRow}>
          <LabelValue
            label="State"
            value={invoice.billingAddress.stateProvinceName}
          />
        </View>

        <LabelValue label="Zip" value={invoice.billingAddress.zipPostalCode} />
      </View>
      <View style={styles.table}>
        <View style={styles.thead} fixed>
          <Text style={[styles.td, styles.cLine, styles.cFirst]}>Line #</Text>
          <Text style={[styles.td, styles.cItem]}>Item #</Text>
          <Text style={[styles.td, styles.cDesc]}>Item Name/Description</Text>
          <Text style={[styles.td, styles.cColor]}>Color</Text>
          <Text style={[styles.td, styles.cQty]}>Qty Ord</Text>
          <Text style={[styles.td, styles.cAvail]}>Qty Avail</Text>
          <Text style={[styles.td, styles.cUom]}>UOM</Text>
          <Text style={[styles.td, styles.cPrice, styles.right]}>Price</Text>
          <Text style={[styles.td, styles.cTotal, styles.right]}>Total</Text>
        </View>

        {invoice.items.map((item, index) => (
          <View key={index} style={styles.tr} wrap={false}>
            <Text style={[styles.td, styles.cLine, styles.cFirst]}>
              {index + 1}
            </Text>
            <Text style={[styles.td, styles.cItem]}>{item.sku}</Text>
            <View style={[styles.td, styles.cDesc]}>
              <Text>{item.productName}</Text>
              {item.isSpecial && (
                <Text style={styles.redText}>Special Order Product</Text>
              )}
            </View>
            <Text style={[styles.td, styles.cColor]}>{item.color || ""}</Text>
            <Text style={[styles.td, styles.cQty]}>{item.quantity}</Text>
            <Text style={[styles.td, styles.cAvail]}>
              {item.quantityShipped}
            </Text>
            <Text style={[styles.td, styles.cUom]}>{item.uom}</Text>
            <Text style={[styles.td, styles.cPrice, styles.right]}>
              {CommonHelper.addDolarSign(item.unitPrice)}
            </Text>
            <Text style={[styles.td, styles.cTotal, styles.right]}>
              {CommonHelper.addDolarSign(item.subTotal)}
            </Text>
          </View>
        ))}
      </View>

      <View style={styles.totalsArea}>
        <View style={styles.totalRow}>
          <Text>Sub-Total:</Text>
          <Text>{CommonHelper.addDolarSign(invoice.orderSubtotal)}</Text>
        </View>
        <View style={styles.totalRow}>
          <Text>Credits / IMPACT Rewards:</Text>
          <Text>
            {invoice.credits === 0
              ? ""
              : CommonHelper.addDolarSign(invoice.credits)}
          </Text>
        </View>
        <View style={styles.totalRow}>
          <Text>Taxes:</Text>
          <Text>{CommonHelper.addDolarSign(invoice.taxes)}</Text>
        </View>
        <View style={styles.totalRow}>
          <Text style={styles.bold}>Order Total:*</Text>
          <Text style={styles.bold}>
            {CommonHelper.addDolarSign(invoice.orderTotal)}
          </Text>
        </View>
        <Text
          style={[
            styles.redText,
            { fontSize: 7, marginTop: 5, fontStyle: "italic" },
          ]}
        >
          Applicable taxes or service charges are not reflected until your order
          is processed.
        </Text>
        <Text style={[{ fontSize: 5, marginTop: 5, fontStyle: "italic" }]}>
          Lansing Building Products cannot guarantee absolute accuracy or be
          held accountable for reporting discrepancies. Please review your
          submissions monthly for complete accuracy{" "}
        </Text>
      </View>
    </Page>
  );
};

export default QualifyingInvoicePagePDF;
